import { useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { useIndividualAppointments } from "./appointments.hooks";
import AppointmentStatusBadge from "@/app/pmc/appointments/components/AppointmentStatusBadge";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import IndividualAppointmentActionsDropdown from "./components/IndividualAppointmentActionsDropdown";
import { ContactPropertyAppointments } from "@/app/contact/contact-property-appointments";
import moment from "moment";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Info } from "lucide-react";
import { appointment_status, AppointmentMetaData } from "@/models/appointment.model";
import { cn } from "@/lib/utils";

export default function IndividualAppointmentsPage() {
  const { list, total, filters, options, pagination, updateAppointmentInList, refreshAppointments } = useIndividualAppointments();
  const [searchParams, setSearchParams] = useSearchParams();

  // On mount: hydrate Redux filters from URL params
  useEffect(() => {
    const q       = searchParams.get("q");
    const status  = searchParams.get("status");
    const service = searchParams.get("service");
    const from    = searchParams.get("from");
    const to      = searchParams.get("to");

    if (q)       filters.setSearch(q);
    if (status)  filters.setStatus(status);
    if (service) filters.setServiceParent(service);
    if (from)    filters.setDateFrom(from);
    if (to)      filters.setDateTo(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync whenever filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search)        params.set("q",       filters.search);
    if (filters.status)        params.set("status",  filters.status);
    if (filters.serviceParent) params.set("service", filters.serviceParent);
    if (filters.dateFrom)      params.set("from",    filters.dateFrom);
    if (filters.dateTo)        params.set("to",      filters.dateTo);
    setSearchParams(params, { replace: true });
  }, [filters.search, filters.status, filters.serviceParent, filters.dateFrom, filters.dateTo]);

  // Convert date strings to Date objects for the filter component using moment
  const dateFrom: Date | undefined = useMemo(() => {
    return filters.dateFrom ? moment(filters.dateFrom, "YYYY-MM-DD").toDate() : undefined;
  }, [filters.dateFrom]);

  const dateTo: Date | undefined = useMemo(() => {
    return filters.dateTo ? moment(filters.dateTo, "YYYY-MM-DD").toDate() : undefined;
  }, [filters.dateTo]);

  const handleDateFromChange = (date: Date | undefined) => {
    filters.setDateFrom(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const handleDateToChange = (date: Date | undefined) => {
    filters.setDateTo(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  // Date validation: if to date is selected, from date cannot be after it
  const getDisabledDatesForFrom = (date: Date): boolean => {
    if (dateTo) {
      return moment(date).isAfter(moment(dateTo), "day");
    }
    return false;
  };

  // Date validation: if from date is selected, to date cannot be before it
  const getDisabledDatesForTo = (date: Date): boolean => {
    if (dateFrom) {
      return moment(date).isBefore(moment(dateFrom), "day");
    }
    return false;
  };

  /* === filters for SimpleTable === */
  const filterItems: FilterItem[] = useMemo(
    () => [
      {
        type: "search",
        title: "Search by property, unit, or service...",
        value: filters.search,
        onValueChange: filters.setSearch,
        grid: 3,
      },
      {
        type: "date",
        title: "From Date",
        value: dateFrom,
        onValueChange: handleDateFromChange,
        grid: 2,
        disabledDates: getDisabledDatesForFrom,
      },
      {
        type: "date",
        title: "To Date",
        value: dateTo,
        onValueChange: handleDateToChange,
        grid: 2,
        disabledDates: getDisabledDatesForTo,
      },
      {
        type: "select",
        title: "Status",
        value: filters.status,
        onValueChange: filters.setStatus,
        options: [
          { value: "pending", label: "Pending" },
          { value: "assigned", label: "Assigned" },
          { value: "accepted", label: "Accepted" },
          { value: "inprogress", label: "In Progress" },
          { value: "goback", label: "Go Back" },
          { value: "rescheduled", label: "Rescheduled" },
          { value: "inspected", label: "Inspected" },
          { value: "done", label: "Done" },
          { value: "completed", label: "Completed" },
          { value: "cancelled", label: "Cancelled" },
        ],
        grid: 2,
      },
      {
        type: "select",
        title: "Service Type",
        value: filters.serviceParent,
        onValueChange: filters.setServiceParent,
        options: options.serviceParents.filter((p) => p && p.trim()).map((p) => ({ label: p, value: p })),
        grid: 2,
      },
    ],
    [
      filters.search,
      filters.status,
      filters.serviceParent,
      filters.dateFrom,
      filters.dateTo,
      options.serviceParents,
      dateFrom,
      dateTo,
    ]
  );

  /* === columns === */
  const cols = useMemo<ColumnDef<AppointmentJoin>[]>(
    () => [
      {
        header: "ID",
        accessor: (r) => {
          const idDisplay = r.id.slice(0, 8).toUpperCase();
          const handleCopy = async (e: React.MouseEvent) => {
            e.stopPropagation();
            try {
              await navigator.clipboard.writeText(r.id);
              toast.success("ID copied to clipboard");
            } catch (error) {
              toast.error("Failed to copy ID");
            }
          };
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleCopy} className="font-mono cursor-pointer hover:underline text-left">
                  {idDisplay}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to copy</p>
              </TooltipContent>
            </Tooltip>
          );
        },
        sortable: true,
        sortKey: "id",
      },
      {
        header: "Property & Unit",
        accessor: (r) => (
          <Link to={`/appointments/${r.id}`} className="flex flex-col hover:underline">
            <span className="font-medium">{r.property.name}</span>
            <span className="text-sm text-muted-foreground">
              Unit {r.property.unit_number} • {r.property.beds} bed • {r.property.baths} bath
            </span>
            {r.property.address && <span className="text-xs text-muted-foreground">{r.property.address}</span>}
          </Link>
        ),
        sortable: true,
        sortKey: "properties.name",
        cellClassName: "hover:bg-purple-50 dark:hover:bg-purple-900",
      },
      {
        header: "Service",
        accessor: (r) => (
          <>
            <div className="font-medium">{r.service.name}</div>
            <div className="text-sm text-muted-foreground">{r.service.parent_name}</div>
            {r.individual_payment != null && (
              <div className="text-sm font-medium text-green-600">Payment: ${r.individual_payment.toFixed(2)}</div>
            )}
          </>
        ),
      },
      {
        header: "Scheduled",
        accessor: (r) =>
          moment(r.date).format("MM/DD/YYYY"),
        sortable: true,
        sortKey: "date",
      },
      {
        header: "Status",
        accessor: (r) => {
          const metaData = (r.meta_data || {}) as AppointmentMetaData;
          const hasReassignmentRequest =
            r.status === appointment_status.pending &&
            metaData.reassignment_requested_by &&
            metaData.reassignment_request_reason;

          return (
            <div className="flex items-center gap-2">
              <AppointmentStatusBadge status={r.status} />
              {hasReassignmentRequest && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-amber-600 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reassignment Requested - {metaData.reassignment_request_reason}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        header: "Actions",
        accessor: (row) => (
          <IndividualAppointmentActionsDropdown
            appointment={row}
            onAppointmentUpdate={updateAppointmentInList}
            onRefetch={refreshAppointments}
          />
        ),
        className: "whitespace-nowrap",
      },
    ],
    [updateAppointmentInList, refreshAppointments]
  );

  return (
    <MainLayout
      breadcrumb={[{ name: "My Appointments", href: "#" }]}
      pageHeader={{
        title: "My Appointments",
        subtitle: "Manage your assigned appointments",
        additionalButtons: (
          <Link to="/appointments/calendar">
            <Button variant="outline" size="sm">
              <CalendarIcon className="mr-2 h-4 w-4" />
              Calendar View
            </Button>
          </Link>
        ),
      }}
    >
      <SimpleTable<AppointmentJoin>
        data={list}
        totalCount={total}
        columns={cols}
        page={pagination.page}
        pageSize={pagination.pageSize}
        setPage={pagination.setPage}
        setPageSize={pagination.setPageSize}
        sortKey={filters.sortKey}
        sortOrder={filters.sortOrder}
        onSortChange={filters.setSortKey}
        filters={filterItems}
        onReset={() => {
          filters.setSearch("");
          filters.setStatus("");
          filters.setServiceParent("");
          filters.setDateFrom("");
          filters.setDateTo("");
        }}
      />
      <ContactPropertyAppointments note="Appointments at these properties are here to read. They aren't your jobs and aren't counted in My Appointments." />
    </MainLayout>
  );
}
